import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import {
	emailGetEmailTemplateScores,
	emailClearEmailTemplateScores
} from '@/modules/email/emailActions';
import type { EmailTemplate } from '@/modules/email/emailTypes';
import type { RootState } from '@/rootReducer';
import {
	LogoNoText,
	PushRightArrow,
	PushLeftArrow,
	ExclamationMark,
	CheckMarkGreen,
	CheckMarkBlue
} from '@/assets/images';
import './EmailSmartTemplates.scss';
import questionMarkCircle from '@/assets/images/question-mark-circle-png.png';

import {
	sendAnalytics,
	sendPublicAnalytics
} from '@/services/analyticsService';

interface Props {
	selectedTemplate: EmailTemplate;
}

const EmailSmartTemplates = ({ selectedTemplate }: Props): JSX.Element => {
	const [
		smartTemplateScores,
		smartTemplateLoading,
		smartTemplateFailure,
		user
	] = useSelector(
		(state: RootState) => [
			state.email.getIn(['templates', 'scores', 'data']).toJS(),
			state.email.getIn(['templates', 'scores', 'loading']),
			state.email.getIn(['templates', 'scores', 'failure']),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const [showSmartTemplates, setShowSmartTemplates] = useState<boolean>(true);
	const dispatch = useDispatch();
	const getEmailTemplateScores = (
		id: number,
		subject: string,
		body: string
	) => dispatch(emailGetEmailTemplateScores({ id, subject, body, user }));
	const clearTemplateScores = () => dispatch(emailClearEmailTemplateScores());

	useEffect(() => {
		clearTemplateScores();
	}, []);

	const getDisplayCount = () => {
		let count = 0;
		if (!smartTemplateScores[`${selectedTemplate.id}`]) {
			return -1;
		}
		if (
			smartTemplateScores[`${selectedTemplate.id}`]['greeting-error'] !==
			''
		) {
			count++;
		}
		if (smartTemplateScores[`${selectedTemplate.id}`].contact === 0) {
			count++;
		}
		if (
			smartTemplateScores[`${selectedTemplate.id}`][
				'blacklist-subject'
			] === 0
		) {
			count++;
		}
		if (
			smartTemplateScores[`${selectedTemplate.id}`]['blacklist-body'] ===
			0
		) {
			count++;
		}
		return count;
	};

	const displayRating = () => {
		if (!smartTemplateScores[`${selectedTemplate.id}`]) {
			return <span className="smart-templates-rating">-</span>;
		}

		const count = getDisplayCount();

		if (count === 0) {
			return <span className="smart-templates-rating good">Good</span>;
		}

		if (count === 1) {
			return (
				<span className="smart-templates-rating better">Better</span>
			);
		}

		if (count === 2) {
			return (
				<span className="smart-templates-rating average">Average</span>
			);
		}

		return <span className="smart-templates-rating poor">Poor</span>;
	};

	const setHoverText = (evt, type) => {
		const el = document.getElementById(`${type}-hovertext`);
		if (el) {
			el.classList.add('smart-templates-hovertext-visible');
		}
	};

	const removeHoverText = (evt, type) => {
		const el = document.getElementById(`${type}-hovertext`);
		if (el) {
			el.classList.remove('smart-templates-hovertext-visible');
		}
	};
	return (
		<div>
			<div className="smart-templates-wrapper">
				{smartTemplateLoading && (
					<div className="smart-templates-loading-background">
						<span className="smart-templates-loading-group">
							<LogoNoText className="smart-templates-loader" />
							Rating template...
						</span>
					</div>
				)}
				<div
					className={`smart-templates flex flex-col ${
						showSmartTemplates ? '' : 'hide'
					}`}
				>
					<div className="smart-templates-section-1">
						<span className="smart-templates-header">
							AI Email Assistant
						</span>
						<span className="smart-templates-rating-holder">
							<span className="smart-templates-rating-header flex flex-row gap-2">
								What's my rating?
								<div
									onMouseEnter={evt =>
										setHoverText(evt, 'header')
									}
									onMouseLeave={evt =>
										removeHoverText(evt, 'header')
									}
								>
									<img
										src={questionMarkCircle}
										alt="question-mark"
										className="smart-templates-question-mark-img"
									/>
								</div>
							</span>
							{displayRating()}
						</span>
					</div>
					<div className="smart-templates-section-2">
						{smartTemplateFailure && (
							<div className="smart-templates-failure-background">
								<span className="smart-templates-failure-text">
									Oops, something went wrong.
									<br />
									<br />
									Please try again by clicking Rate Template
									below.
								</span>
							</div>
						)}
						<div className="smart-templates-section-header">
							Subject
						</div>
						<div className="smart-templates-greeting">
							<div
								className={`smart-templates-word-count-upper ${
									getDisplayCount() >= 0
										? 'smart-templates-results-arrived-text'
										: ''
								}`}
							>
								<span className="smart-templates-greeting-header-group">
									Word Count
									<div
										className="smart-templates-subject-length-question-mark"
										onMouseEnter={evt => {
											setHoverText(evt, 'subject-length');
										}}
										onMouseLeave={evt => {
											removeHoverText(
												evt,
												'subject-length'
											);
										}}
									>
										<img src={questionMarkCircle} />
									</div>
								</span>
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] >= 2 &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] < 9 && (
										<CheckMarkGreen className="smart-templates-checkmark" />
									)}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									(smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] < 2 ||
										smartTemplateScores[
											`${selectedTemplate.id}`
										]['subject-word-count'] > 8) && (
										<ExclamationMark className="smart-templates-exclamation" />
									)}
							</div>
							<div className="smart-templates-greeting-lower">
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] >= 2 &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] < 9 &&
									`Looking good!`}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									(smartTemplateScores[
										`${selectedTemplate.id}`
									]['subject-word-count'] < 2 ||
										smartTemplateScores[
											`${selectedTemplate.id}`
										]['subject-word-count'] > 8) &&
									`We recommend a subject between 2 and 8 words.`}
							</div>
						</div>
						<div className="smart-templates-spam">
							<div
								className={`smart-templates-spam-upper ${
									getDisplayCount() >= 0
										? 'smart-templates-results-arrived-text'
										: ''
								}`}
							>
								<span className="smart-templates-spam-header-group">
									Spam words
									<div
										className="smart-templates-spam-question-mark"
										onMouseEnter={evt => {
											setHoverText(evt, 'spam-subject');
										}}
										onMouseLeave={evt => {
											removeHoverText(
												evt,
												'spam-subject'
											);
										}}
									>
										<img
											src={questionMarkCircle}
											className="smart-templates-spam-question-mark"
										/>
									</div>
								</span>
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-subject'
								] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-subject'
								]?.length > 1 ? (
									<ExclamationMark className="smart-templates-exclamation" />
								) : (
									smartTemplateScores[
										`${selectedTemplate.id}`
									] && (
										<CheckMarkGreen className="smart-templates-checkmark" />
									)
								)}
							</div>
							{smartTemplateScores[`${selectedTemplate.id}`] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-subject'
								]?.length > 1 && (
									<div className="smart-templates-spam-lower">
										{`Your email subject has words that commonly trigger spam filters: ${smartTemplateScores[
											`${selectedTemplate.id}`
										]['blacklist-words-subject'].join(
											', '
										)}`}
									</div>
								)}
							{smartTemplateScores[`${selectedTemplate.id}`] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-subject'
								] > 0 && (
									<div className="smart-templates-spam-lower">
										No spam words detected.
									</div>
								)}
						</div>
						<div
							className={`smart-templates-border-block ${
								getDisplayCount() >= 0
									? 'smart-templates-results-border-block'
									: ''
							}`}
						/>
						<div className="smart-templates-section-header">
							Body
						</div>
						<div className="smart-templates-contact">
							<div
								className={`smart-templates-contact-upper ${
									getDisplayCount() >= 0
										? 'smart-templates-results-arrived-text'
										: ''
								}`}
							>
								<span className="smart-templates-contact-header-group">
									Contact
									<div
										className="smart-templates-contact-question-mark"
										onMouseEnter={evt => {
											setHoverText(evt, 'contact');
										}}
										onMouseLeave={evt => {
											removeHoverText(evt, 'contact');
										}}
									>
										<img
											src={questionMarkCircle}
											className="smart-templates-contact-question-mark"
										/>
									</div>
								</span>
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									].contact > 0 && (
										<CheckMarkGreen className="smart-templates-checkmark" />
									)}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									].contact === 0 && (
										<ExclamationMark className="smart-templates-exclamation" />
									)}
							</div>
							<div className="smart-templates-contact-lower">
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									].contact > 0 &&
									'Looking good!'}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									].contact === 0 &&
									'Include a phone number in the signature of your email.'}
							</div>
						</div>
						<div className="smart-templates-greeting">
							<div
								className={`smart-templates-greeting-upper ${
									getDisplayCount() >= 0
										? 'smart-templates-results-arrived-text'
										: ''
								}`}
							>
								<span className="smart-templates-greeting-header-group">
									Greeting
									<div
										onMouseEnter={evt => {
											setHoverText(evt, 'greeting');
										}}
										onMouseLeave={evt => {
											removeHoverText(evt, 'greeting');
										}}
									>
										<div className="smart-templates-greeting-question-mark">
											<img
												src={questionMarkCircle}
												className="smart-templates-greeting-question-mark"
											/>
										</div>
									</div>
								</span>
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['greeting-error'] === '' && (
										<CheckMarkGreen className="smart-templates-checkmark" />
									)}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['greeting-error'] !== '' && (
										<ExclamationMark className="smart-templates-exclamation" />
									)}
							</div>
							<div className="smart-templates-greeting-lower">
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['greeting-error'] === '' &&
									'Well done!'}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['greeting-error'] ===
										'No greeting detected.' &&
									'We recommend including a simple greeting in your template.'}
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
									smartTemplateScores[
										`${selectedTemplate.id}`
									]['greeting-error'] ===
										'Business name in greeting.' &&
									'We recommend against using the business’s name in your template greeting.'}
							</div>
						</div>
						<div className="smart-templates-spam">
							<div
								className={`smart-templates-spam-upper ${
									getDisplayCount() >= 0
										? 'smart-templates-results-arrived-text'
										: ''
								}`}
							>
								<span className="smart-templates-spam-header-group">
									Spam words
									<div
										onMouseEnter={evt => {
											setHoverText(evt, 'spam-body');
										}}
										onMouseLeave={evt => {
											removeHoverText(evt, 'spam-body');
										}}
									>
										<div className="smart-templates-spam-question-mark">
											<img
												src={questionMarkCircle}
												className="smart-templates-spam-question-mark"
											/>
										</div>
									</div>
								</span>
								{smartTemplateScores[
									`${selectedTemplate.id}`
								] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-body'
								] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-body'
								]?.length > 1 ? (
									<ExclamationMark className="smart-templates-exclamation" />
								) : (
									smartTemplateScores[
										`${selectedTemplate.id}`
									] && (
										<CheckMarkGreen className="smart-templates-checkmark" />
									)
								)}
							</div>
							{smartTemplateScores[`${selectedTemplate.id}`]&&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-words-body'
								]?.length > 1 && (
									<div className="smart-templates-spam-lower">
										{`Your email body has words that commonly trigger spam filters: ${smartTemplateScores[
											`${selectedTemplate.id}`
										]['blacklist-words-body'].join(', ')}`}
									</div>
								)}
							{smartTemplateScores[`${selectedTemplate.id}`] &&
								smartTemplateScores[`${selectedTemplate.id}`][
									'blacklist-body'
								] > 0 && (
									<div className="smart-templates-spam-lower">
										No spam words detected.
									</div>
								)}
							<div
								className={`smart-templates-border-block ${
									getDisplayCount() >= 0
										? 'smart-templates-results-border-block'
										: ''
								}`}
							/>
						</div>
					</div>
					<div className="smart-templates-section-3">
						<button
							className="smart-templates-button"
							onClick={() => {
								getEmailTemplateScores(
									selectedTemplate.id,
									selectedTemplate.subject,
									selectedTemplate.body.replace(
										/<[^>]*>?/gm,
										''
									)
								);
								if (user && user.email) {
									sendAnalytics({
										type: 'track',
										userId: user.email,
										properties: {
										templateGraderRedesign: user?.features?.templateRedesign
									},
									eventName:
											'User Graded a Templated with Template Grader'
									});
								}
							}}
						>
							Rate Template
							<CheckMarkBlue className="smart-templates-button-checkmark" />
						</button>
						<div className="smart-templates-more-information">
							Visit our{' '}
							<a href="https://learn.re2.ai/send-out-high-quality-emails-through-resquared">
								Knowledge Base
							</a>{' '}
							to learn more about the templates
						</div>
					</div>
				</div>
				<span
					className="smart-templates-subject-length-hovertext"
					id="subject-length-hovertext"
				>
					Resquared recommends have subject line lengths of less than
					9 words, ideally between 2 and 8 words.
				</span>
				<span
					className="smart-templates-spam-hovertext"
					id="spam-subject-hovertext"
				>
					Resquared checks your email subject for words that commonly
					trigger spam filters.
				</span>
				<span
					className="smart-templates-contact-hovertext"
					id="contact-hovertext"
				>
					Resquared checks whether you’ve included a phone number.
					This gives recipients a good way to contact you and makes
					your email look more reputable.
				</span>
				<span
					className="smart-templates-greeting-hovertext"
					id="greeting-hovertext"
				>
					Resquared checks whether you’ve included a greeting in your
					email. We also check that there is NOT a business' name in
					the greeting, as we've found this to have a negative affect.
				</span>
				<span
					className="smart-templates-spam-body-hovertext"
					id="spam-body-hovertext"
				>
					Resquared checks your email for words that commonly trigger
					spam filters.
				</span>
				<span
					className="smart-templates-header-hovertext"
					id="header-hovertext"
				>
					{getDisplayCount() === -1 &&
						`Resquared calculates your score based on some simple elements discovered via our proprietary machine learning-based research. Note: We do not check spelling and grammar.`}
					{getDisplayCount() === 0 &&
						`Great job! You've included all the elements we've seen increase responses to your emails. That doesn't mean it's perfect, please still check language and grammar.`}
					{getDisplayCount() === 1 &&
						`Not bad, but you're missing at least one of the elements we've seen increase responses to your emails. Also, please remember that we do not check language and grammar.`}
					{getDisplayCount() === 2 &&
						`Not bad, but you're missing at least two of the elements we've seen increase responses to your emails. Also, please remember that we do not check language and grammar.`}
					{getDisplayCount() >= 3 &&
						`Needs Work. You're missing at least three of the elements we've seen increase responses to your emails. Also, please remember that we do not check language and grammar.`}
				</span>
			</div>
		</div>
	);
};

export default EmailSmartTemplates;
